import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useMatch } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import FormArea from '../../components/FormArea';
import FormInput from '../../components/FormInput';
import FormLabel from '../../components/FormLabel';
import FormSelect from '../../components/FormSelect';
import SimpleButton from '../../components/SimpleButton';
import Spacer from '../../components/Spacer';
import { IApplicationState } from '../../store';
import { sweepRoomItemDraftUpdate, sweepsSaveRoomItemRequest } from '../../store/sweeps/slice';
import { ISweepRoomItem } from '../../store/sweeps/types';
import { Container } from './styles';

const ItemForm: React.FC = (props) => {
    const route = useMatch('/item/:idSweep/:idRoom/:idItem/form');
    const routeRegister = useMatch('/item/:idSweep/:idRoom/form');

    const { t } = useTranslation();

    const idSweep = route?.params.idSweep || routeRegister?.params.idSweep;
    const idRoom = route?.params.idRoom || routeRegister?.params.idRoom;
    const idItem = route?.params.idItem;

    const sweep = useSelector((state: IApplicationState) => state.sweeps.data.find(sweep => sweep.id === idSweep))

    const room = sweep?.rooms.find(room => room.id === idRoom);

    const item = room?.items.find(item => item.id === idItem);

    const sweeps = useSelector((state: IApplicationState) => state.sweeps);

    const dispatch = useDispatch();

    const [isLoaded, setIsLoaded] = useState(false);

    const editingItem = sweeps.draftRoomItem;

    const setEditingItem = useCallback((item: ISweepRoomItem) => {
        dispatch(sweepRoomItemDraftUpdate(item))
    }, [dispatch])

    useEffect(() => {
        if (item && !isLoaded) {
            setIsLoaded(true)

            setEditingItem({
                ...item,
                error: undefined
            })
        }
    }, [isLoaded, item, setEditingItem])

    if (!editingItem) {
        return <Container>
            <h1>{t("pages.items.not_found_form")}</h1>
        </Container>
    }

    return <Container>
        <h1>{item ? t("pages.items.edit_item") : t("pages.items.create_item")}</h1>

        <FormLabel>{t("pages.items.item_name")}</FormLabel>
        <FormInput
            value={editingItem?.name || ""}
            onChangeText={(text) => {
                setEditingItem({
                    ...editingItem,
                    name: text
                })
            }}
        />

        <FormLabel>{t("pages.items.amount")}</FormLabel>
        <FormInput
            value={String(editingItem?.amount || "") || ""}
            type={'text'}
            onChangeText={(text) => {
                setEditingItem({
                    ...editingItem,
                    amount: parseInt(text.replace(',', '.') || "") || 0
                })
            }}
        />

        <FormLabel>{t("pages.items.risk")}</FormLabel>
        <FormSelect
            value={String(editingItem?.risk || "0") || "1"}
            onChangeText={(text) => {
                setEditingItem({
                    ...editingItem,
                    risk: text === '2' ? 2 : text === '1' ? 1 : 0
                })
            }} >
            <option value={'0'}>{t("pages.items.low")}</option>
            <option value={'1'}>{t("pages.items.medium")}</option>
            <option value={'2'}>{t("pages.items.high")}</option>
        </FormSelect>

        <FormLabel>{t("pages.items.open")}</FormLabel>
        <FormSelect
            value={editingItem?.open ? '1' : '0'}
            onChangeText={(text) => {
                setEditingItem({
                    ...editingItem,
                    open: text === '1' ? true : false
                })
            }} >
            <option value={'1'}>{t("common.yes")}</option>
            <option value={'0'}>{t("common.no")}</option>
        </FormSelect>

        <FormLabel>{t("pages.items.sealed")}</FormLabel>
        <FormSelect
            value={editingItem?.sealed ? '1' : '0'}
            onChangeText={(text) => {
                setEditingItem({
                    ...editingItem,
                    sealed: text === '1' ? true : false
                })
            }} >
            <option value={'1'}>{t("common.yes")}</option>
            <option value={'0'}>{t("common.no")}</option>
        </FormSelect>

        <FormLabel>{t("pages.items.jeopardized")}</FormLabel>
        <FormSelect
            value={editingItem?.jeopardized ? '1' : '0'}
            onChangeText={(text) => {
                setEditingItem({
                    ...editingItem,
                    jeopardized: text === '1' ? true : false
                })
            }} >
            <option value={'1'}>{t("common.yes")}</option>
            <option value={'0'}>{t("common.no")}</option>
        </FormSelect>

        <FormLabel>{t("pages.items.observations")}</FormLabel>
        <FormArea
            value={String(editingItem.observations || "") || ""}
            onChangeText={(text) => {
                setEditingItem({
                    ...editingItem,
                    observations: text
                })
            }} />

        <FormLabel>{t("pages.items.informations")}</FormLabel>
        <FormArea
            value={String(editingItem.info || "") || ""}
            onChangeText={(text) => {
                setEditingItem({
                    ...editingItem,
                    info: text
                })
            }} />


        <Spacer size={20} />

        <SimpleButton
            prependIcon='save'
            color='primary'
            disabled={editingItem.isSaving}
            onClick={() => {
                dispatch(sweepsSaveRoomItemRequest(editingItem))
            }}>
            {editingItem.isSaving ? `${t("common.buttons.saving")}...` : t("common.buttons.save")}
        </SimpleButton>

        <Spacer size={20} />

        {editingItem.error !== undefined && <ErrorMessage>{editingItem.error.message || t("common.errors.unexpected")}</ErrorMessage>}
    </Container>;
}

export default ItemForm;